import React, { ComponentType, ReactNode, useMemo } from 'react';

import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useStoreWithValidation,
} from '@adsk/alloy-react-context';

import { TOOL_IDS } from './consts';
import { useCurrentToolIdStore } from './stores/CurrentToolIdStore';

export type ToolIdsType = typeof TOOL_IDS;
export type ToolIdType = ToolIdsType[keyof ToolIdsType];

type CurrentToolIdContextType = {
  toolId?: ToolIdType;
};

/**
 * Context for current tool ID.
 */
const CurrentToolIdContext = getOrCreateGlobalContext<CurrentToolIdContextType>(
  CONTEXT_NAMES.CurrentToolIdContext,
);

const useCurrentToolIdStoreWithValidation = () =>
  useStoreWithValidation<CurrentToolIdContextType>(
    CONTEXT_NAMES.CurrentToolIdContext,
    {
      hook: 'useCurrentToolIdContext',
      provider: 'CurrentToolIdProvider',
      context: CurrentToolIdContext,
    },
  );

/**
 * Returns the current tool ID from context. Must be used inside `CurrentToolIdProvider`.
 */
export const useCurrentToolId = () => {
  const { toolId } = useCurrentToolIdStoreWithValidation();
  return toolId;
};

type CurrentToolIdProviderProps = {
  toolId?: ToolIdType;
  children: ReactNode;
};

export const CurrentToolIdProvider: ComponentType<
  CurrentToolIdProviderProps
> = ({ children, toolId: toolIdProp }) => {
  useCurrentToolIdStore(toolIdProp);

  const value = useMemo(() => ({ toolId: toolIdProp }), [toolIdProp]);

  return (
    <CurrentToolIdContext.Provider value={value}>
      {children}
    </CurrentToolIdContext.Provider>
  );
};

CurrentToolIdProvider.displayName = 'CurrentToolIdProvider';
